import { useTranslation } from "react-i18next";
import {
  CloseIcon,
  DuplicateIcon,
  RotateLeftIcon,
  RotateRightIcon,
  TrashIcon,
} from "../../../../components/restaurant-schema/schemaIcons";

const OUTLINE_BUTTON =
  "flex h-9 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-[9px] border bg-surface text-[12.5px] font-semibold transition-colors duration-150";

/**
 * Рамката на инспектора (дизайн, т. 7): хедър с чип/заглавие/подзаглавие и
 * затваряне, полетата (children), завъртане ±45° и Дублирай/Изтрий отдолу.
 * Общ за маса и обект.
 */
export const InspectorCard = ({
  badge,
  title,
  subtitle,
  rotation,
  onRotate,
  onDuplicate,
  onDelete,
  onClose,
  children,
}: {
  badge: React.ReactNode;
  title: string;
  subtitle: string;
  rotation: number;
  onRotate: (direction: 1 | -1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <section className="flex flex-none flex-col rounded-[14px] border border-shade-2 bg-surface">
      <div className="flex items-center gap-3 border-b border-shade-2 px-4 py-3.5">
        {badge}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="truncate text-[15px] font-bold leading-[1.25] text-ink">{title}</div>
          <div className="truncate text-[12.5px] leading-[1.3] text-body">{subtitle}</div>
        </div>
        <button
          type="button"
          title={t("common.close")}
          aria-label={t("common.close")}
          onClick={onClose}
          className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-[7px] border-none bg-transparent text-muted transition-colors duration-150 hover:bg-fill hover:text-ink"
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex flex-col gap-3.5 px-4 pb-4 pt-3.5">
        {children}
        <div className="flex flex-col gap-[7px]">
          <div className="text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1px] text-muted">
            {t("restaurantSchema.inspector.rotation")}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              title="−45°"
              onClick={() => onRotate(-1)}
              className={`${OUTLINE_BUTTON} border-shade-3 text-ink-2 hover:bg-panel`}
            >
              <RotateLeftIcon className="h-[15px] w-[15px]" />
              45°
            </button>
            <span className="w-14 text-center text-[15px] font-bold tabular-nums text-ink">
              {rotation}°
            </span>
            <button
              type="button"
              title="+45°"
              onClick={() => onRotate(1)}
              className={`${OUTLINE_BUTTON} border-shade-3 text-ink-2 hover:bg-panel`}
            >
              45°
              <RotateRightIcon className="h-[15px] w-[15px]" />
            </button>
          </div>
        </div>
        <div className="flex gap-2 border-t border-shade-2 pt-3.5">
          <button
            type="button"
            onClick={onDuplicate}
            className={`${OUTLINE_BUTTON} h-[38px] border-shade-3 text-[13px] text-ink hover:bg-panel`}
          >
            <DuplicateIcon className="h-[15px] w-[15px]" />
            {t("restaurantSchema.toolbar.duplicateShort")}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className={`${OUTLINE_BUTTON} h-[38px] border-danger-ink/25 text-[13px] text-danger-ink hover:bg-danger-tint/60`}
          >
            <TrashIcon className="h-[15px] w-[15px]" />
            {t("restaurantSchema.toolbar.deleteShort")}
          </button>
        </div>
      </div>
    </section>
  );
};
